import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, Form } from 'antd';
import getFormData from 'get-form-data';

import FieldsGroup from '@atoms/form/fields-group';
import ConfirmExchange from '@organisms/exchange/ConfirmExchange';
import getAccounts from '@search/exchange';
import notification from '@helps/notification';

import { fieldChange, fieldValidation, responseError, getFields } from 'FIELDS';
import { tConsume } from 'TRANSLATION';
import { SelectSet, RadioSet, InputsSet } from 'INPUTS';
import defaults from 'DEFAULTS';
import api from 'API';
import { connect } from 'react-redux';

class ExchangeForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      error: false,
      retryCount: 0,
      retryLimit: 3,
      countdown: 0,
      countdownInterval: null,

      currency_pair: {
        name: 'currency_pair',
        keys: [],
        required: true,
        disableSearch: false,
        label: props.t('field.currency_pair'),
      },
      currency_to: {
        name: 'currency_to',
        keys: [],
        required: true,
        disableSearch: true,
        label: props.t('field.currency_to'),
      },
      wallet_id: {
        name: 'wallet_id',
        keys: [],
        currency_pair: null,
        required: true,
        disableSearch: true,
        label: props.t('field.source_account'),
        disabled: { currency_pair: null },
      },
      action: {
        name: 'action',
        value: props.vendor === 'INTERBANK' ? 'BUY' : 'SELL',
        disabled: false,
        label: false,
        required: true,
        notField: true,
        keys: props.t('keys.action'),
      },
      amount: {
        name: 'amount',
        label: props.t('field.amount'),
        required: true,
        min: 0,
        placeholder: true,
        description: null,
        validation: {
          isDisableNumber: {
            error: 'nullValue',
          },
          length: true,
        },
      },
      rate: {
        name: 'rate',
        required: props.userType === 'aemi',
        type: 'number',
        label: props.t('field.rate'),
        placeholder: true,
      },
      rateData: null,
      data: null,
      limits: null,
    };

    this._mounted = false;

    this.handleFieldChange = this.handleFieldChange.bind(this, this);
    this.fieldValidation = fieldValidation.bind(this, this);
    this.responseError = responseError.bind(this, this);
    this.getFields = getFields.bind(this, this);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.getCurrencyPair = this.getCurrencyPair.bind(this);
    this.toggleModalWatcher = this.toggleModalWatcher.bind(this);
    this.startCountdown = this.startCountdown.bind(this);
    this.clearCountdown = this.clearCountdown.bind(this);
    this.handleSendReport = this.handleSendReport.bind(this);
  }

  componentDidMount() {
    this._mounted = true;

    this.getCurrencyPair();
  }

  componentWillUnmount() {
    this._mounted = false;
    this.clearCountdown();
  }

  startCountdown() {
    this.clearCountdown();
    this.setState({ countdown: 10 }, () => {
      const countdownInterval = setInterval(() => {
        this.setState((prevState) => {
          if (prevState.countdown <= 1) {
            clearInterval(this.state.countdownInterval);
            return { countdown: 0, countdownInterval: null };
          }
          return { countdown: prevState.countdown - 1 };
        });
      }, 1000);
      this.setState({ countdownInterval });
    });
  }

  clearCountdown() {
    if (this.state.countdownInterval) {
      clearInterval(this.state.countdownInterval);
      this.setState({ countdownInterval: null, countdown: 0 });
    }
  }

  async getCurrencyPair() {
    const { currency_pair } = this.state;
    const { userType, personalData, vendor, data } = this.props;

    try {
      this.setState({ loading: true });

      let CONFIG;

      if (userType === 'aemi') {
        CONFIG = api.banking.aemi.markupsInfo(personalData?.business_profile_id).AXIOS('GET');
      } else {
        CONFIG = api.banking.client.currencyPair().AXIOS('GET', { vendor: vendor });
      }

      const result = await axios(CONFIG);
      if (this._mounted) {
        const list = result.data.pairs.map((item) => ({
          title: item,
          value: item,
        }));

        this.setState({
          currency_pair: {
            ...currency_pair,
            keys: list,
          },
          limits: result.data.limits,
        });

        if (data) {
          this.setState(
            {
              currency_pair: {
                ...this.state.currency_pair,
                value: data?.currency_pair,
              },
              wallet_id: {
                ...this.state.wallet_id,
                value: data?.wallet_id,
                currency_pair: data?.currency_pair,
              },
              action: {
                ...this.state.action,
                value: data?.action,
              },
              currency_to: {
                ...this.state.currency_to,
                value: data?.currency_to,
                keys: data?.currency_pair.split('/'),
              },
              amount: {
                ...this.state.amount,
                value: data?.amount,
              },
              rate: {
                ...this.state.rate,
                value: data?.rate,
              },
            },
            () =>
              getAccounts(this, '', {
                state: this.state.wallet_id,
                props: {
                  t: this.props.t,
                  item: { appendKeys: [] },
                },
              })
          );
          this.setState({
            wallet_id: {
              ...this.state.wallet_id,
              value: data.wallet_id,
              currency_pair: data.currency_pair,
            },
          });
        }
      }
    } catch (error) {
      if (this._mounted) {
        this.responseError(error);
      }
    } finally {
      if (this._mounted) {
        this.setState({ loading: false });
      }
    }
  }

  async submitCalculationRequest(formData) {
    const { userType, vendor } = this.props;
    const dataForRequest = { ...formData };

    const { amount, currency_pair, ...rest } = dataForRequest;
    const data = {
      ...rest,
      amount: Number(amount.replace(/ /g, '')),
      currency_from: currency_pair.split('/')[0],
      currency_to: currency_pair.split('/')[1],
    };

    try {
      this.setState({ loading: true, error: false });

      let CONFIG;

      if (userType === 'aemi') {
        CONFIG = api.banking.client.aemiCalculation().AXIOS('POST', data);
      } else {
        CONFIG = api.banking.client.calculation().AXIOS('POST', { ...data, vendor });
      }

      const result = await axios(CONFIG);
      if (this._mounted) {
        this.clearCountdown();

        this.setState({ rateData: result.data.data, data: dataForRequest, retryCount: 0 });
      }
    } catch (error) {
      if (this._mounted) {
        this.responseError(error);
        if (error.response && error.response.status === 409) {
          const { retryCount, retryLimit } = this.state;
          if (retryCount < retryLimit) {
            this.setState({ retryCount: retryCount + 1, error: true });
            this.startCountdown();
          } else {
            this.setState({ error: true });
          }
        }
      }
    } finally {
      if (this._mounted) {
        this.setState({ loading: false });
      }
    }
  }

  async handleSendReport() {
    const { userType } = this.props;

    if (userType === 'aemi') {
      if (this._mounted) {
        notification('This feature is not available for AEMI', 'error');
      }
      return;
    }

    const { vendor, toggleModal } = this.props;
    const dataForRequest = { ...this.state.data };

    const { amount, currency_pair, ...rest } = dataForRequest;
    const data = {
      ...rest,
      amount: Number(amount.replace(/ /g, '')),
      currency_from: currency_pair.split('/')[0],
      currency_to: currency_pair.split('/')[1],
    };

    let CONFIG = api.banking.client.calculationReport().AXIOS('POST', {
      vendor,
      action: data.action,
      amount: data.amount,
      currency_to: data.currency_to,
      currency_from: data.currency_from,
    });

    try {
      const result = await axios(CONFIG);

      if (this._mounted) {
        notification(result.data.message, 'success');

        toggleModal();
      }
    } catch (error) {
      if (this._mounted) {
        this.responseError(error);
      }
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    const { loading, retryCount, retryLimit } = this.state;
    if (retryCount >= retryLimit) {
      return;
    }
    if (loading) return;
    if (!this.fieldValidation()) {
      return;
    }
    const formData = getFormData(event.target, { trim: true });
    this.setState({ data: formData });
    await this.submitCalculationRequest(formData);
  }

  componentDidUpdate(prevProps, prevState) {
    const { amount } = this.state;

    const prevAmountValue = prevState.amount.value;
    const currentAmountValue = amount.value;

    function formatNumberWithSpaces(value) {
      if (typeof value === 'number') {
        value = value.toString();
      }

      if (!value) return value;

      value = value.replace(/,/g, '.');

      value = value.replace(/[^\d.]/g, '').replace(/\.(?=.*\.)/g, '');

      value = value.replace(/\s+/g, '');

      const [integerPart, decimalPart] = value.split('.');

      const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

      return decimalPart !== undefined ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
    }

    if (currentAmountValue !== prevAmountValue) {
      const formattedValue = formatNumberWithSpaces(currentAmountValue);

      this.setState({
        amount: {
          ...amount,
          value: formattedValue,
        },
      });
    }
  }

  handleFieldChange(self, data) {
    const { currency_pair, currency_to, action, wallet_id } = this.state;

    if (data.name === 'currency_pair') {
      this.setState(
        {
          wallet_id: {
            ...wallet_id,
            currency_pair: data.value,
            value: undefined,
          },
        },
        () => {
          getAccounts(this, '', {
            state: this.state.wallet_id,
            props: {
              t: this.props.t,
              item: {
                appendKeys: [],
              },
            },
          });
        }
      );

      if (action.value === 'SELL') {
        this.setState({
          currency_to: {
            ...currency_to,
            disabled: false,
            value: data.value.split('/')[0],
            keys: data.value.split('/'),
          },
        });
      } else if (action.value === 'BUY') {
        this.setState({
          currency_to: {
            ...currency_to,
            disabled: false,
            value: data.value.split('/')[1],
            keys: data.value.split('/'),
          },
        });
      }
    }

    if (data.name === 'action') {
      if (data.value && currency_pair.value) {
        if (data.value === 'SELL') {
          this.setState({
            currency_to: {
              ...currency_to,
              value: currency_pair.value.split('/')[0],
            },
          });
        } else if (data.value === 'BUY') {
          this.setState({
            currency_to: {
              ...currency_to,
              value: currency_pair.value.split('/')[1],
            },
          });
        }
      }
    }

    if (data.name === 'currency_to') {
      if (data.value) {
        const { value: pair, keys: pairs } = currency_pair;
        const [currency_from, currency_to] = pair.split('/');
        const newPair = `${currency_to}/${currency_from}`;

        const updatedPairs = pairs.map((item) => (item.value === pair ? { value: newPair, title: newPair } : item));

        this.setState({
          currency_pair: {
            ...currency_pair,
            disabled: false,
            value: newPair,
            keys: updatedPairs,
          },
        });
      }
    }

    fieldChange(self, data);
  }

  toggleModalWatcher(data) {
    if (data) {
      this.props.toggleModal(false, data);
      return;
    }
    this.props.toggleModal();
  }

  render() {
    const { t, userType, getData, vendor } = this.props;
    const { rateData, data, limits, currency_to, loading, error, retryCount, retryLimit, countdown } = this.state;
    if (rateData) {
      return (
        <ConfirmExchange
          toggleModal={this.toggleModalWatcher}
          userType={userType}
          rateData={rateData}
          getData={getData}
          vendor={vendor}
          data={data}
        />
      );
    }

    const maxValue =
      userType !== 'aemi' && currency_to?.value && limits.find((i) => i?.currency === currency_to?.value.split('/')[0]);

    return (
      <>
        <Form onSubmit={this.handleSubmit}>
          <FieldsGroup>
            {SelectSet(this, ['currency_pair', 'wallet_id'])}
            {RadioSet(this, ['action'])}
            {SelectSet(this, ['currency_to'])}
            {userType === 'aemi' && InputsSet(this, ['rate'])}
            {InputsSet(this, ['amount'])}
            {userType !== 'aemi' && currency_to?.value && (
              <div style={{ color: 'red', marginBottom: 20 }}>
                *
                <span style={{ color: 'gray', marginLeft: 5 }}>
                  Amount cannot exceed {maxValue?.value} {maxValue?.currency}
                </span>
              </div>
            )}

            {error && retryCount < retryLimit && countdown > 0 && (
              <span style={{ color: 'red', marginBottom: 10 }}>
                {t('notification.getRate.message')} {countdown}
              </span>
            )}

            {retryCount >= retryLimit && (
              <span style={{ color: 'red', marginBottom: 10 }}>{t('notification.sendReport.message')}</span>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {retryCount < retryLimit && (
                <Button
                  type={'primary'}
                  htmlType={'submit'}
                  size={defaults.antd.button.size}
                  block
                  disabled={loading || (error && countdown > 0)}
                >
                  {t('button.create')}
                </Button>
              )}
              {retryCount >= retryLimit && (
                <Button
                  type={'danger'}
                  size={defaults.antd.button.size}
                  block
                  onClick={this.handleSendReport}
                  disabled={loading}
                >
                  Send report
                </Button>
              )}
            </div>
          </FieldsGroup>
        </Form>
      </>
    );
  }
}

const mapStateToProps = ({ personal }) => ({
  personalData: personal.data,
});

export default tConsume(connect(mapStateToProps)(ExchangeForm));

ExchangeForm.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  personalData: PropTypes.object.isRequired,
  getData: PropTypes.func.isRequired,
  userType: PropTypes.string.isRequired,
  vendor: PropTypes.string,
  t: PropTypes.func.isRequired,
  data: PropTypes.object,
};
